import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { scrollAndFocus } from 'platform/utilities/scroll';

import ArtifactSummary from './ArtifactSummaryReview';
import { pageHasErrors } from '../domain/artifacts';

/**
 * Standalone in-flow page shown after the upload step. It presents the
 * CAVE-extracted document data (DD-214 / death certificate) for review and
 * inline editing, then lets the user continue.
 *
 * The actual extracted-data rendering and editing is delegated to the
 * embeddable `<ArtifactSummary />`; this wrapper only adds the page heading,
 * intro, missing-information alert, and the Continue/Back navigation.
 *
 * `goForward` / `goBack` are injected by forms-system when used as a
 * `CustomPage`.
 */
const ArtifactReviewPage = ({ goForward, goBack }) => {
  const formData = useSelector(state => state?.form?.data ?? {});
  const files = formData.files ?? [];
  const pageRef = useRef(null);

  const hasErrors = pageHasErrors(files);

  const handleContinue = () => {
    if (hasErrors) {
      // Errors are already visible; scroll to and focus the first document with
      // missing information, mirroring normal forms-system validation behavior.
      const firstError = pageRef.current?.querySelector(
        '[data-has-errors="true"]',
      );
      if (firstError) scrollAndFocus(firstError);
      return;
    }
    goForward({ formData });
  };

  return (
    <div ref={pageRef}>
      <h3 className="vads-u-font-size--h3 vads-u-margin-bottom--1">
        Review your uploaded documents
      </h3>
      <p className="vads-u-margin-bottom--3">
        Review the information we extracted from your uploaded documents. You
        can edit any fields that need to be corrected before submitting.
      </p>
      {hasErrors && (
        <va-alert status="error" uswds class="vads-u-margin-bottom--3">
          <h2 slot="headline">Add missing information</h2>
          <p>
            Edit and add information in the highlighted fields below. Any
            missing details can typically be found in the document you uploaded.
          </p>
        </va-alert>
      )}

      <ArtifactSummary showErrors variant="accordion" />

      {goForward && (
        <div className="vads-u-margin-top--4 vads-u-margin-bottom--4 vads-u-display--flex vads-u-flex-direction--row">
          {goBack && (
            <va-button
              back
              onClick={goBack}
              uswds
              data-dd-action-name="Artifact Review Back"
            />
          )}
          <va-button
            continue
            onClick={handleContinue}
            uswds
            class="vads-u-margin-left--1"
            data-dd-action-name="Artifact Review Continue"
          />
        </div>
      )}
    </div>
  );
};

ArtifactReviewPage.propTypes = {
  goBack: PropTypes.func,
  goForward: PropTypes.func,
};

export default ArtifactReviewPage;
